import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getPlanUsages, getPlans } from '../../../services/saas';
import { showAlert } from '../../../store/ducks/alert';
import { DataGrid, ProgressBar, Text } from '@getpingback/realeza';
import { addDotAfterThreeDigits } from '../../../helpers/utils';
import { formatCurrencyToFront } from '../../../helpers/formatCurrency';
import { InfinityIcon } from '@stash-ui/solid-icons';
const PlanComsumptionTable = ({ channelUid }) => {
  const dispatch = useDispatch();
  const [planComsumption, setPlanComsumption] = useState([]);
  const [planInfo, setPlanInfo] = useState([]);

  useEffect(() => {
    getPlanComsumption();
    getPlansInfo();
  }, []);

  const getPlanComsumption = async () => {
    try {
      const { data } = await getPlanUsages(channelUid);
      setPlanComsumption(data);
    } catch (error) {
      dispatch(showAlert('error', error.message));
      console.log(error);
    }
  };

  const getPlansInfo = async () => {
    try {
      const { data } = await getPlans(channelUid);
      setPlanInfo(data);
    } catch (error) {
      dispatch(showAlert('error', error.message));
      console.log(error);
    }
  };

  const handleLimitColor = percentUsed => {
    if (percentUsed === 0) {
      return {
        color: 'gray',
        intensity: 500,
      };
    } else if (percentUsed < 35) {
      return {
        color: 'green',
        intensity: 500,
      };
    } else if (percentUsed < 55) {
      return {
        color: 'green',
        intensity: 300,
      };
    } else if (percentUsed < 65) {
      return {
        color: 'yellow',
        intensity: 200,
      };
    } else if (percentUsed < 75) {
      return {
        color: 'orange',
        intensity: 400,
      };
    } else {
      return {
        color: 'red',
        intensity: 500,
      };
    }
  };

  const WidgetLimit = ({ value }) => {
    const colors = handleLimitColor(value);

    return (
      <div className="saas-drawer__widget">
        <div className="saas-drawer__widget--text">
          {value === -2 ? (
            <div className="flex text-nowrap items-center">
              <InfinityIcon color="#52525b" width={18} height={18} />
            </div>
          ) : (
            <Text size={12} weight="bold" color="gray" intensity={600}>
              {value}%
            </Text>
          )}
        </div>

        <ProgressBar data-testid="progress-bar" percent={value === -2 ? 0 : value} bgColor={colors.color} bgIntensity={colors.intensity} />
      </div>
    );
  };

  const getPercentage = feature => {
    if (feature.limit === 0) return 100;
    else if (feature.limit === -2) return -2;
    else {
      const percentage = (feature.usage / feature.limit) * 100;
      return percentage.toFixed(0);
    }
  };

  const COLUMNS = [
    {
      field: 'functionality',
      headerName: 'Functionality',
      width: 180,
      txtColor: 'gray',
      txtColorIntensity: 400,
      txtOpacity: 0.45,
      fontSize: 14,
      fontWeight: 600,
    },
    { field: 'comsumption', headerName: 'Comsumption', width: 130, fontSize: 14, txtColor: 'gray', txtColorIntensity: 400 },
    {
      field: 'limit',
      headerName: 'Limit',
      width: 90,
      txtColor: 'green',
      txtColorIntensity: 400,
      txtOpacity: 0.85,
      fontSize: 14,
      fontWeight: 400,
    },
    { field: 'percentage', headerName: '', width: '25%' },
  ];

  const plansKeys = Object.keys(planComsumption).filter(key => key !== 'success' && key !== 'customTemplateEmail');

  const ROWS = plansKeys.map(key => ({
    functionality: key,
    comsumption: addDotAfterThreeDigits(planComsumption[key].usage),
    limit: planComsumption[key].limit === -2 ? <InfinityIcon width={18} height={18} /> : addDotAfterThreeDigits(planComsumption[key].limit),
    percentage: <WidgetLimit value={getPercentage(planComsumption[key])} />,
  }));

  return (
    <div className="plan-comsumption">
      <div className="plan-comsumption__info">
        <div className="plan-comsumption__info__content">
          <span>
            <Text size={14} weight="bold" color="gray" intensity={400}>
              Plan Name:
            </Text>
            <Text size={14} weight="regular" color="gray" intensity={200}>
              {planInfo?.tier?.SaasPlan?.name}
            </Text>
          </span>
          <span>
            <Text size={14} weight="bold" color="gray" intensity={400}>
              Plan Status:
            </Text>
            <Text size={14} weight="semibold" color={planInfo?.paidSaas?.status === 'ACTIVE' ? 'green' : 'red'} intensity={500}>
              {planInfo?.paidSaas?.status}
            </Text>
          </span>
          <span>
            <Text size={14} weight="bold" color="gray" intensity={400}>
              Tier:
            </Text>
            <Text size={14} weight="regular" color="gray" intensity={300}>
              {planInfo?.tier?.SaasPlan.tier}
            </Text>
          </span>
          <span>
            <Text size={14} weight="bold" color="gray" intensity={400}>
              Price:
            </Text>
            <Text size={14} weight="regular" color="gray" intensity={300}>
              {formatCurrencyToFront(planInfo?.tier?.SaasPlan?.value)}
            </Text>
          </span>
          <span>
            <Text size={14} weight="bold" color="gray" intensity={400}>
              Discounts:
            </Text>
            {planInfo?.discounts?.length > 0 ? (
              <Text size={14} weight="regular" color="gray" intensity={300}>
                {planInfo?.discounts?.map((discount, index) => (
                  <span>
                    {discount.name} - {discount.percentOff ? discount.percentOff : discount.amountOff}
                    {index < planInfo?.discounts.length - 1 ? ' · ' : ''}
                  </span>
                ))}
              </Text>
            ) : (
              <Text size={14} weight="regular" color="gray" intensity={300}>
                0
              </Text>
            )}
          </span>
          <span>
            <Text size={14} weight="bold" color="gray" intensity={400}>
              Addons:
            </Text>
            {planInfo?.addons?.length > 0 ? (
              <Text size={14} weight="regular" color="gray" intensity={300}>
                {planInfo?.addons?.map((addons, index) => (
                  <span>
                    {addons.SaasPlan.name} - {formatCurrencyToFront(addons.SaasPlan.value)}
                    {index < planInfo?.addons.length - 1 ? ' · ' : ''}
                  </span>
                ))}
              </Text>
            ) : (
              <Text size={14} weight="regular" color="gray" intensity={300}>
                0
              </Text>
            )}
          </span>
          <span>
            <Text size={14} weight="bold" color="gray" intensity={400}>
              Plan Id:
            </Text>
            <Text size={14} weight="regular" color="gray" intensity={300}>
              {planInfo?.tier?.SaasPlan?.id}
            </Text>
          </span>
          <span>
            <Text size={14} weight="bold" color="gray" intensity={400}>
              subscriptionToken:
            </Text>
            <Text size={14} weight="regular" color="gray" intensity={300}>
              {planInfo?.paidSaas?.subscriptionToken}
            </Text>
          </span>
          <span>
            <Text size={14} weight="bold" color="gray" intensity={400}>
              Periods of months:
            </Text>
            <Text size={14} weight="regular" color="gray" intensity={300}>
              {planInfo?.tier?.SaasPlan?.periodOfMonths}
            </Text>
          </span>

          <span>
            <Text size={14} weight="bold" color="gray" intensity={400}>
              Created At:
            </Text>
            <Text size={14} weight="regular" color="gray" intensity={300}>
              {new Date(planInfo?.tier?.createdAt).toLocaleDateString('pt-BR')}
            </Text>
          </span>
          <span>
            <Text size={14} weight="bold" color="gray" intensity={400}>
              Deleted At:
            </Text>
            <Text size={14} weight="regular" color="gray" intensity={300}>
              {planInfo?.tier?.deletedAt ? new Date(planInfo?.tier?.deletedAt).toLocaleDateString('pt-BR') : '-'}
            </Text>
          </span>
        </div>
      </div>

      <DataGrid rows={ROWS} columns={COLUMNS} />
    </div>
  );
};

export default PlanComsumptionTable;
